// @ts-strict-ignore
import { BaseArticleManager } from '@project/components/article/manager/base-article-manager';
import { CloneUtils } from '@shared/util/clone-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleManager } from '@article/manager/article.manager';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { Subscription } from 'rxjs';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { StringUtils } from '@shared/util/string-utils';

export class CoverLabelStripArticleManager extends BaseArticleManager {
  private _articleUsageToCreate: ArticleUsageVO;
  private _articleInsertedSubscription: Subscription;


  constructor(
    projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    undoRedoHelper: UndoRedoHelper,
    alertHelper: AlertHelper,
    undoRedoUtils: UndoRedoUtils,
    insertArticleUtils: InsertArticleUtils,
    private selectionModelService: SelectionModelService,
    private articleManager: ArticleManager,
    private _insertArticleService: InsertArticleService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    super(projectModel, undoRedoHelper, alertHelper, undoRedoUtils, insertArticleUtils);
  }

  public async recreateArticle(articleUsage: ArticleUsageVO, length: number, offset: number): Promise<void> {
    this.selectionModelService.removeAllSelections();

    const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    const parentUsage: ArticleUsageVO = this.getParentArticleUsage(articleUsage);
    if (ArticleUtils.isCoverLabelStripArticle(article) && parentUsage) {
      await this.startRecreateOperation(article, articleUsage, parentUsage, length, offset);
    }
  }

  private async startRecreateOperation(article: ArticleVO, articleUsage: ArticleUsageVO, parentUsage: ArticleUsageVO, length: number,
    offset: number): Promise<void> {
    this._articleUsageToCreate = !articleUsage ? null : CloneUtils.cloneArticleUsageVO(articleUsage);
    if (!this._articleUsageToCreate) {
      this._articleUsageToCreate = this.projectArticleService.createUnstoredNewArticleUsage(article.identity, article.version);
    }
    ArticleUsageUtils.replaceArticleUsageId([this._articleUsageToCreate], this._articleUsageToCreate.id, StringUtils.createUuid());
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(this._articleUsageToCreate, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH, length);
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(this._articleUsageToCreate, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET, offset);
    this.projectArticleService.storeArticleUsage(this._articleUsageToCreate);

    this._detachOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds([articleUsage.id]);
    const detachedArticle = await this.detachArticleUsageService.detachArticleUsageFromProjectById(articleUsage.id);
    if (detachedArticle) {
      this.applicationRefreshService.notifyProjectChange();
    }
    await this.insertCoverLabelStripArticle(article, parentUsage, offset);
  }

  private async insertCoverLabelStripArticle(dynamicLabelingArticle: ArticleVO, parentUsage: ArticleUsageVO, offset: number): Promise<void> {
    this._articleLocation = this.createOnRailArticleLocation(parentUsage, offset);
    this._articleLocation.existingArticleUsageId = this._articleUsageToCreate.id;
    try {
      const data = await this._insertArticleService.insertArticleByArticleLocation(dynamicLabelingArticle, this._articleLocation, 'insertCoverLabelStripArticle');
      await this.insertCoverLabelStripArticle_success(data)
    } catch (error) {
      await this.insertCoverLabelStripArticle_failure(error)
    }
  }

  private async insertCoverLabelStripArticle_success(data: InsertedArticleData): Promise<void> {
    if (!data.articleLocation || data.articleLocation !== this._articleLocation) {
      return;
    }
    await this.pushInsertArticleOperation(data.insertedArticleGUIDs, data.moveArticlesMap);
    this.reset();
  }

  private async insertCoverLabelStripArticle_failure(messageData: any): Promise<void> {
    await this.finalizeFailureProcessingArticle('dynamicLAbelingArticleManager.error.title',
      messageData && messageData.message ? messageData.message : '');
  }

  private clear(): void {
    this._articleUsageToCreate = null;
  }

  protected reset(): void {
    super.reset();
    this.clear();
    this._articleInsertedSubscription?.unsubscribe();
    this._articleInsertedSubscription = undefined;
  }
}
